// /opt/ptg01-v1/front-extranet/app/auth/login/page.tsx
'use client';

import { useState, Suspense } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { NexviaLogo } from '@nexvia/ui';
import { Lock } from 'lucide-react';

function LoginForm() {
    const router = useRouter();
    const searchParams = useSearchParams();
    const sessionReason = searchParams.get('reason');
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(sessionReason === 'session' ? 'Votre session a expiré. Vous vous êtes connecté depuis un autre appareil.' : '');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const res = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',   // 🔒 reçoit le cookie httpOnly nx_token
                body: JSON.stringify(formData),
            });

            const result = await res.json();

            if (result.success) {
                // Token JWT stocké en cookie httpOnly par le backend (setAuthCookie).
                // On ne persiste QUE les métadonnées user pour UX (fast initial render).
                localStorage.setItem('user', JSON.stringify(result.user));
                // Token JWT stocké en cookie httpOnly par le backend (setAuthCookie).
                // Plus besoin de localStorage.setItem('token') — toutes les fetches
                // passent par fetchExtranet() qui envoie le cookie automatiquement.

                if (result.user.mustChangePassword) {
                    router.push('/auth/change-password');
                } else if (result.user.status !== 'ACTIVE') {
                    router.push('/onboarding');
                } else {
                    router.push('/');
                }
            } else if (result.emailNotVerified) {
                setError('Veuillez vérifier votre adresse email. Consultez votre boîte de réception.');
            } else {
                setError(result.message || 'Identifiants incorrects');
            }
        } catch {
            setError('Erreur de communication avec le serveur');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
            <div className="max-w-md w-full space-y-8">

                {/* Logo */}
                <div className="flex justify-center">
                    <NexviaLogo />
                </div>

                {/* Card */}
                <div className="bg-white rounded-2xl p-8 shadow-xl border border-slate-200">
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-50 rounded-full mb-4">
                            <Lock className="w-5 h-5 text-[#000091]" />
                        </div>
                        <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-sans">Connexion</h1>
                        <p className="text-slate-500 mt-2 text-sm font-sans">Accédez à votre espace consultant</p>
                    </div>

                    {error && (
                        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm font-sans">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700 font-sans">Email</label>
                            <input
                                type="email" required
                                data-testid="login-email"
                                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#000091] focus:border-[#000091] transition-all font-sans"
                                placeholder="votre.email@exemple.com"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-slate-700 font-sans">Mot de passe</label>
                            <input
                                type="password" required
                                data-testid="login-password"
                                className="w-full px-4 py-3 bg-white border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#000091] focus:border-[#000091] transition-all font-sans"
                                placeholder="••••••••"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                            />
                        </div>

                        <button
                            type="submit" disabled={loading}
                            data-testid="login-submit"
                            className="w-full py-3.5 bg-[#000091] hover:bg-[#1e40af] text-white font-bold rounded-lg shadow-sm transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed font-sans"
                        >
                            {loading ? "Connexion..." : "Se connecter"}
                        </button>

                        <p className="text-center">
                            <Link href="/auth/forgot-password" className="text-[#000091] hover:text-[#1e40af] font-semibold text-sm transition-colors font-sans">
                                Mot de passe oubli&eacute; ?
                            </Link>
                        </p>

                        <p className="text-center text-slate-500 text-sm font-sans">
                            Pas encore de compte ?{' '}
                            <Link href="/register" className="text-[#000091] hover:text-[#1e40af] font-semibold transition-colors">
                                S&apos;inscrire
                            </Link>
                        </p>
                    </form>
                </div>

                {/* Footer */}
                <p className="text-center text-xs text-slate-600 font-sans">
                    &copy; {new Date().getFullYear()} Nexvia Portage SAS — Données hébergées en France
                </p>
            </div>
        </div>
    );
}

export default function LoginPage() {
    return (
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="text-slate-400">Chargement...</div></div>}>
            <LoginForm />
        </Suspense>
    );
}
